import React from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { colors, fontSizes, fontWeights, radius, spacing } from '../theme';
import type { TimeSlot } from '../data/shopData';

interface TimeSlotPickerProps {
  slots: TimeSlot[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function TimeSlotPicker({ slots, selectedId, onSelect }: TimeSlotPickerProps) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.container}
    >
      {slots.map(slot => {
        const isSelected = slot.id === selectedId;
        return (
          <TouchableOpacity
            key={slot.id}
            style={[
              styles.chip,
              isSelected && styles.chipSelected,
              !slot.available && styles.chipDisabled,
            ]}
            onPress={() => slot.available && onSelect(slot.id)}
            disabled={!slot.available}
            activeOpacity={0.75}
          >
            <Text
              style={[
                styles.label,
                isSelected && styles.labelSelected,
                !slot.available && styles.labelDisabled,
              ]}
            >
              {slot.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );
}

// Wrapper with label for use in sections
interface TimeSectionProps extends TimeSlotPickerProps {
  visible: boolean;
}

export function TimeSlotsSection({ visible, ...props }: TimeSectionProps) {
  if (!visible) {
    return null;
  }
  return (
    <View style={styles.section}>
      <TimeSlotPicker {...props} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  chip: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.pill,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    marginRight: spacing.sm,
  },
  chipSelected: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
  },
  chipDisabled: {
    opacity: 0.35,
  },
  label: {
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.medium,
    color: colors.textPrimary,
  },
  labelSelected: {
    color: colors.white,
  },
  labelDisabled: {
    color: colors.textMuted,
  },
  section: {
    marginHorizontal: -spacing.md,
  },
});
